import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { API_URL } from "../../../../config";
import {
  mapPostedSalesInvoiceRows,
  summarizeRevenueProfit,
  formatCurrency,
} from "./revenueProfitHelpers";
import "./RevenueProfit.css";

// "Revenue & Profit" widget — actual Revenue (excl. tax) and actual Profit from
// invoiced jobs, for Month-To-Date and Year-To-Date.
//
// Role behaviour follows the same dataScope contract as the other widgets
// (no role names hardcoded):
//   dataScope === "self" (Sales): only the logged-in salesperson's invoiced
//     jobs — Revenue MTD/YTD and Profit MTD/YTD KPI cards.
//   dataScope === "all" (Sales Director / President): overall totals + a
//     per-salesperson breakdown table.
//
// Live data comes from the PostedSalesInvoices endpoint; each row is mapped
// (mapPostedSalesInvoiceRows) into the record shape summarizeRevenueProfit
// consumes, so the aggregation and UI below stay source-agnostic.

const ZERO_TOTALS = { revenueMtd: 0, revenueYtd: 0, profitMtd: 0, profitYtd: 0 };

// Data layer: pull invoiced-job rows from the dashboard endpoint and normalize
// them for the aggregation. `salesPersonId` (Sales / self scope) narrows to a
// single rep; `signal` aborts the in-flight request on unmount.
const fetchRevenueProfit = async ({ token, salesPersonId, signal }) => {
  const res = await axios.get(
    `${API_URL}/Dashboard/api/Dashboard/PostedSalesInvoices`,
    { headers: { Authorization: `Bearer ${token}` }, signal }
  );
  let records = mapPostedSalesInvoiceRows(res && res.data ? res.data.result : []);
  const mergedRecord = [...records];

  return salesPersonId
    ? mergedRecord.filter((r) => r.salesPerson === salesPersonId)
    : mergedRecord;
};

const RevenueProfit = ({ auth: { auth }, dataScope = "all", userId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const token = auth && auth[0] ? auth[0].token : null;
  const isSelf = dataScope === "self";
  const salesPersonId = isSelf ? userId : null;

  useEffect(() => {
    if (!token) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchRevenueProfit({ token, salesPersonId, signal: controller.signal })
      .then((records) => {
        if (cancelled) return;
        setSummary(summarizeRevenueProfit(records));
      })
      .catch((err) => {
        if (cancelled || axios.isCancel(err)) return;
        console.warn("Could not load Revenue & Profit:", err.message);
        setError(true);
        setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token, salesPersonId]);

  const totals = summary ? summary.totals : ZERO_TOTALS;
  const reps = summary ? summary.reps : [];

  const card = (label, value) => (
    <div className="rp-card">
      <div className="rp-card-value">{formatCurrency(value)}</div>
      <div className="rp-card-label">{label}</div>
    </div>
  );

  // Revenue + Profit KPI groups (used by both roles for the top section).
  const totalsSection = (
    <div className="rp-groups">
      <div className="rp-group">
        <div className="rp-group-title">Revenue</div>
        <div className="rp-cards">
          {card("Month To Date", totals.revenueMtd)}
          {card("Year To Date", totals.revenueYtd)}
        </div>
      </div>
      <div className="rp-group">
        <div className="rp-group-title">Profit</div>
        <div className="rp-cards">
          {card("Month To Date", totals.profitMtd)}
          {card("Year To Date", totals.profitYtd)}
        </div>
      </div>
    </div>
  );

  return (
    <div className="rp-widget">
      <h4 className="rp-title">Revenue &amp; Profit</h4>

      {loading ? (
        <div className="rp-state">Loading…</div>
      ) : error ? (
        <div className="rp-state rp-error">Unable to load data.</div>
      ) : isSelf ? (
        // Sales: own Revenue / Profit KPI cards.
        totalsSection
      ) : (
        // Sales Director / President: overall totals + per-rep breakdown.
        <div className="rp-overview">
          {totalsSection}
          <table className="rp-table">
            <thead>
              <tr>
                <th className="rp-rowhead">Salesperson</th>
                <th>Revenue MTD</th>
                <th>Profit MTD</th>
                <th>Revenue YTD</th>
                <th>Profit YTD</th>
              </tr>
            </thead>
            <tbody>
              {reps.length > 0 ? (
                reps.map((rep) => (
                  <tr key={rep.repName}>
                    <td className="rp-rep">{rep.repName}</td>
                    <td className="rp-num">{formatCurrency(rep.revenueMtd)}</td>
                    <td className="rp-num">{formatCurrency(rep.profitMtd)}</td>
                    <td className="rp-num">{formatCurrency(rep.revenueYtd)}</td>
                    <td className="rp-num">{formatCurrency(rep.profitYtd)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="rp-empty">
                    No invoiced jobs.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });

export { RevenueProfit };

export default connect(mapStateToProps)(RevenueProfit);
